import { useState, useEffect, useRef } from "react";
import { searchCountries, searchCities, getCitiesForCountry } from "../data/locations";
import { ChevronDown } from "lucide-react";

export function CountryAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Select country...",
  className = "",
  required = false,
  "data-testid": dataTestId,
}) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setQuery(inputValue);
    
    if (inputValue.length >= 1) {
      const results = searchCountries(inputValue);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (country) => {
    setQuery(country);
    onChange(country);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleBlur = () => {
    // Only update if we have a valid input
    setTimeout(() => {
      if (query && query !== value) {
        onChange(query);
      }
    }, 200);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.length >= 1) {
              const results = searchCountries(query);
              setSuggestions(results);
              setShowSuggestions(results.length > 0);
            }
          }}
          onBlur={handleBlur}
          placeholder={placeholder}
          data-testid={dataTestId}
          className={`w-full px-3 py-2 pr-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
          required={required}
        />
        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((country, index) => (
            <div
              key={country}
              onClick={() => handleSelect(country)}
              className={`px-3 py-2 cursor-pointer transition-colors ${
                index === selectedIndex
                  ? "bg-blue-500 text-white"
                  : "hover:bg-blue-50"
              }`}
            >
              {country}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CityAutocomplete({ 
  value, 
  onChange, 
  country = null,
  placeholder = "Select city...",
  className = "",
  required = false,
  "data-testid": dataTestId,
}) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Reset when country changes
  useEffect(() => {
    if (country && query) {
      const citiesForCountry = getCitiesForCountry(country);
      if (citiesForCountry.length > 0 && !citiesForCountry.includes(query)) {
        // Don't auto-clear - let user decide
      }
    }
  }, [country, query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setQuery(inputValue);
    
    if (inputValue.length >= 1) {
      const results = searchCities(inputValue, country);
      // Format results
      const formattedResults = country 
        ? results.map(city => typeof city === 'string' ? city : city.city)
        : results.map(r => typeof r === 'string' ? r : `${r.city}, ${r.country}`);
      setSuggestions(formattedResults);
      setShowSuggestions(formattedResults.length > 0);
      setSelectedIndex(-1);
    } else if (country) {
      // Show all cities for the country when no query
      const citiesForCountry = getCitiesForCountry(country);
      setSuggestions(citiesForCountry.slice(0, 10));
      setShowSuggestions(citiesForCountry.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (city) => {
    // Extract just the city name if it includes country
    const cityName = city.includes(',') ? city.split(',')[0].trim() : city;
    setQuery(cityName);
    onChange(cityName);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (query.length >= 1) {
      const results = searchCities(query, country);
      const formattedResults = country 
        ? results.map(city => typeof city === 'string' ? city : city.city)
        : results.map(r => typeof r === 'string' ? r : `${r.city}, ${r.country}`);
      setSuggestions(formattedResults);
      setShowSuggestions(formattedResults.length > 0);
    } else if (country) {
      const citiesForCountry = getCitiesForCountry(country);
      setSuggestions(citiesForCountry.slice(0, 10));
      setShowSuggestions(citiesForCountry.length > 0);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (query && query !== value) {
        onChange(query);
      }
    }, 200);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          data-testid={dataTestId}
          className={`w-full px-3 py-2 pr-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
          required={required}
        />
        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((city, index) => (
            <div
              key={`${city}-${index}`}
              onClick={() => handleSelect(city)}
              className={`px-3 py-2 cursor-pointer transition-colors ${
                index === selectedIndex
                  ? "bg-blue-500 text-white"
                  : "hover:bg-blue-50"
              }`}
            >
              {city}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default { CountryAutocomplete, CityAutocomplete };
